/**
 * @fileoverview Own the managed icon vocabulary and compatibility with legacy Prime icon names. `kc-internal-frontend/src/components/shared/AppIcon.js` consumes these mappings and defaults; screens should express icon intent through that boundary. Central aliases let the application change icon presentation without editing every consuming workspace.
 */

import {
    ArrowCounterClockwise,
    ArrowLeft,
    ArrowRight,
    ArrowSquareOut,
    ArrowUp,
    ArrowUpRight,
    ArrowsClockwise,
    ArrowsDownUp,
    ArrowsOut,
    AppWindow,
    Bell,
    BookOpen,
    Briefcase,
    Buildings,
    Calculator,
    CalendarBlank,
    CalendarMinus,
    CalendarPlus,
    Camera,
    CaretDown,
    CaretLeft,
    CaretRight,
    CaretUp,
    CaretUpDown,
    ChartBar,
    ChartLine,
    Chats,
    Check,
    CheckCircle,
    CheckSquare,
    Circle,
    Clipboard,
    Clock,
    ClockCounterClockwise,
    Cloud,
    CloudArrowDown,
    CloudArrowUp,
    Code,
    Copy,
    CreditCard,
    CurrencyDollar,
    Database,
    DotsThree,
    DotsThreeVertical,
    DownloadSimple,
    Envelope,
    Eye,
    EyeSlash,
    FileAudio,
    FileArrowUp,
    FileCode,
    FileCsv,
    FileImage,
    FileMagnifyingGlass,
    FilePdf,
    FilePlus,
    FilePpt,
    FileVideo,
    FileXls,
    FileZip,
    FastForward,
    FileText,
    Flag,
    FloppyDisk,
    Folder,
    FolderMinus,
    FolderOpen,
    FolderPlus,
    Funnel,
    FunnelSimple,
    FunnelX,
    Gear,
    GitBranch,
    Hash,
    Heart,
    House,
    Image,
    Info,
    Lightning,
    Link,
    List,
    ListChecks,
    Lock,
    LockOpen,
    MagnifyingGlass,
    MapPin,
    MicrosoftWordLogo,
    Microphone,
    Minus,
    MinusCircle,
    Moon,
    NotePencil,
    Package,
    Paperclip,
    PaperPlaneTilt,
    Pause,
    PauseCircle,
    PencilSimple,
    Phone,
    Play,
    Plus,
    PlusCircle,
    Power,
    Printer,
    Prohibit,
    Question,
    PushPin,
    Scissors,
    ShareNetwork,
    ShieldCheck,
    SignOut,
    SlidersHorizontal,
    SortAscending,
    SortDescending,
    SpinnerGap,
    Sparkle,
    SquaresFour,
    Star,
    Stop,
    Table,
    Tag,
    TagSimple,
    TextAlignJustify,
    Trash,
    Tray,
    TreeStructure,
    UploadSimple,
    User,
    UserGear,
    UserMinus,
    UserPlus,
    Users,
    Wallet,
    Warning,
    WarningCircle,
    Wrench,
    X,
    XCircle,
} from '@phosphor-icons/react'

export const phosphorIconSettings = {
    size: '1em',
    weight: 'fill',
    mirrored: false,
    className: 'app-icon',
    spinClassName: 'app-icon--spin',
    fallbackIcon: Question,
}

const FILLED_WEIGHT = 'fill'
const BOLD_WEIGHT = 'bold'



function GoogleLogo({ size = '1em', color, mirrored, weight, ...rest }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" {...rest}>
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill={color || '#4285F4'} />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill={color || '#34A853'} />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 0 0 0 12c0 1.94.46 3.77 1.28 5.4l3.56-2.77.01-.54z" fill={color || '#FBBC05'} />
            <path d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.09 14.97 0 12 0 7.7 0 3.99 2.47 2.18 6.07l3.66 2.84c.87-2.6 3.3-4.16 6.16-4.16z" fill={color || '#EA4335'} />
        </svg>
    )
}

export const managedIconMap = {
    google: { icon: GoogleLogo, weight: 'regular' },
    'brand-google': { icon: GoogleLogo, weight: 'regular' },
}

export const legacyPrimeIconMap = {
    'pi-angle-down': CaretDown,
    'pi-angle-right': CaretRight,
    'pi-align-justify': TextAlignJustify,
    'pi-arrow-left': ArrowLeft,
    'pi-arrow-right': ArrowRight,
    'pi-arrow-up': ArrowUp,
    'pi-arrow-up-right': ArrowUpRight,
    'pi-arrows-alt': ArrowsOut,
    'pi-arrows-v': ArrowsDownUp,
    'pi-ban': Prohibit,
    'pi-bars': List,
    'pi-bell': Bell,
    'pi-bolt': Lightning,
    'pi-book': BookOpen,
    'pi-box': Package,
    'pi-briefcase': Briefcase,
    'pi-building': Buildings,
    'pi-calculator': Calculator,
    'pi-calendar': CalendarBlank,
    'pi-calendar-minus': CalendarMinus,
    'pi-calendar-plus': CalendarPlus,
    'pi-camera': Camera,
    'pi-chart-bar': ChartBar,
    'pi-chart-line': ChartLine,
    'pi-check': Check,
    'pi-check-circle': CheckCircle,
    'pi-check-square': CheckSquare,
    'pi-chevron-down': CaretDown,
    'pi-chevron-left': CaretLeft,
    'pi-chevron-right': CaretRight,
    'pi-chevron-up': CaretUp,
    'pi-circle': Circle,
    'pi-circle-fill': { icon: Circle, weight: FILLED_WEIGHT },
    'pi-clipboard': Clipboard,
    'pi-clock': Clock,
    'pi-cloud': Cloud,
    'pi-cloud-download': CloudArrowDown,
    'pi-cloud-upload': CloudArrowUp,
    'pi-clone': Copy,
    'pi-cog': Gear,
    'pi-code': Code,
    'pi-code-branch': GitBranch,
    'pi-comments': Chats,
    'pi-copy': Copy,
    'pi-credit-card': CreditCard,
    'pi-database': Database,
    'pi-dollar': CurrencyDollar,
    'pi-download': DownloadSimple,
    'pi-ellipsis-h': DotsThree,
    'pi-ellipsis-v': DotsThreeVertical,
    'pi-envelope': Envelope,
    'pi-exclamation-circle': WarningCircle,
    'pi-exclamation-triangle': Warning,
    'pi-external-link': ArrowSquareOut,
    'pi-eye': Eye,
    'pi-eye-slash': EyeSlash,
    'pi-file': FileText,
    'pi-file-archive': FileZip,
    'pi-file-audio': FileAudio,
    'pi-file-check': FileText,
    'pi-file-code': FileCode,
    'pi-file-csv': FileCsv,
    'pi-file-edit': NotePencil,
    'pi-file-image': FileImage,
    'pi-file-import': FileArrowUp,
    'pi-file-pdf': FilePdf,
    'pi-file-plus': FilePlus,
    'pi-file-powerpoint': FilePpt,
    'pi-file-search': FileMagnifyingGlass,
    'pi-file-video': FileVideo,
    'pi-file-word': MicrosoftWordLogo,
    'pi-file-excel': FileXls,
    'pi-filter': Funnel,
    'pi-filter-fill': { icon: FunnelSimple, weight: FILLED_WEIGHT },
    'pi-filter-slash': FunnelX,
    'pi-flag': Flag,
    'pi-folder': Folder,
    'pi-folder-minus': FolderMinus,
    'pi-folder-open': FolderOpen,
    'pi-folder-plus': FolderPlus,
    'pi-forward': FastForward,
    'pi-hashtag': Hash,
    'pi-heart': Heart,
    'pi-history': ClockCounterClockwise,
    'pi-home': House,
    'pi-inbox': Tray,
    'pi-info-circle': Info,
    'pi-image': Image,
    'pi-link': Link,
    'pi-list': List,
    'pi-list-check': ListChecks,
    'pi-lock': Lock,
    'pi-lock-open': LockOpen,
    'pi-map-marker': MapPin,
    'pi-minus': Minus,
    'pi-minus-circle': MinusCircle,
    'pi-microphone': Microphone,
    'pi-moon': Moon,
    'pi-pause': Pause,
    'pi-pause-circle': PauseCircle,
    'pi-paperclip': Paperclip,
    'pi-pencil': PencilSimple,
    'pi-phone': Phone,
    'pi-play': Play,
    'pi-plus': Plus,
    'pi-plus-circle': PlusCircle,
    'pi-power-off': Power,
    'pi-print': Printer,
    'pi-question': Question,
    'pi-question-circle': Question,
    'pi-refresh': { icon: ArrowsClockwise, weight: BOLD_WEIGHT },
    'pi-replay': { icon: ArrowCounterClockwise, weight: BOLD_WEIGHT },
    'pi-save': FloppyDisk,
    'pi-scissors': Scissors,
    'pi-search': MagnifyingGlass,
    'pi-send': PaperPlaneTilt,
    'pi-share-alt': ShareNetwork,
    'pi-shield': ShieldCheck,
    'pi-sign-out': SignOut,
    'pi-sitemap': TreeStructure,
    'pi-sliders-h': SlidersHorizontal,
    'pi-sort-alt': CaretUpDown,
    'pi-sort-amount-down': SortDescending,
    'pi-sort-amount-up': SortAscending,
    'pi-sort-amount-up-alt': SortAscending,
    'pi-spinner': SpinnerGap,
    'pi-sparkles': Sparkle,
    'pi-star': Star,
    'pi-star-fill': { icon: Star, weight: FILLED_WEIGHT },
    'pi-stop': Stop,
    'pi-sync': { icon: ArrowsClockwise, weight: BOLD_WEIGHT },
    'pi-table': Table,
    'pi-tag': Tag,
    'pi-tags': TagSimple,
    'pi-times': X,
    'pi-times-circle': XCircle,
    'pi-th-large': SquaresFour,
    'pi-thumbtack': PushPin,
    'pi-trash': Trash,
    'pi-undo': { icon: ArrowCounterClockwise, weight: BOLD_WEIGHT },
    'pi-upload': UploadSimple,
    'pi-user': User,
    'pi-user-edit': UserGear,
    'pi-user-minus': UserMinus,
    'pi-user-plus': UserPlus,
    'pi-users': Users,
    'pi-wallet': Wallet,
    'pi-wrench': Wrench,
    'pi-window-maximize': AppWindow,
}



export function resolveManagedIcon(iconName) {
    if (!iconName) return null

    const normalized = String(iconName).trim().toLowerCase()
    const mappedIcon = managedIconMap[normalized]

    if (!mappedIcon) return null

    if (mappedIcon.icon) {
        return mappedIcon
    }

    return { icon: mappedIcon }
}



export function resolveLegacyPrimeIcon(iconClass) {
    if (!iconClass) return null

    const normalized = String(iconClass).trim().toLowerCase()
    const mappedIcon = legacyPrimeIconMap[normalized]

    if (!mappedIcon) return null

    if (mappedIcon.icon) {
        return mappedIcon
    }

    return { icon: mappedIcon }
}
